/**
 * BROWSERZUSTAND DES BOARDS (F19, Ruling A5; Muster `panelSpeicher.ts`): Filter, Darstellung
 * (je Spalte | Raster) und das Auf-/Zuklappen der Spalten liegen im Browser, nicht in der
 * Datenbank — geräteabhängige Bequemlichkeit. Jeder Wert wird beim Lesen geprüft (er kann
 * fehlen, veraltet oder von Hand verändert sein), jeder Zugriff steht in try/catch (im
 * privaten Fenster wirft schon der Zugriff auf `localStorage`). Scheitert etwas, gilt die
 * Vorgabe.
 */

export const ANSICHT_SCHLUESSEL = "ki-tickets-ansicht";
export const FILTER_SCHLUESSEL = "ki-tickets-filter";
export const OFFEN_SCHLUESSEL = "ki-tickets-offen";

export type BoardFilter = { company: string; customer: string; assignee: string; linked: boolean };

/** Gemerkt werden nur Kennungen und Schalter, nie Namen von Kunden oder Personen (Review 12):
 *  der Browserspeicher ist kein Ort für Personendaten. Die Namen kommen beim Laden vom Server. */
export const LEERER_FILTER: BoardFilter = { company: "ALLE", customer: "", assignee: "", linked: false };

function lies(schluessel: string): unknown {
  try {
    const roh = localStorage.getItem(schluessel);
    return roh === null ? null : JSON.parse(roh) as unknown;
  } catch {
    return null;
  }
}

function schreib(schluessel: string, wert: string): void {
  try {
    localStorage.setItem(schluessel, wert);
  } catch {
    // Privater Modus oder blockierter Speicher — der Zustand gilt dann nur für diese Sitzung.
  }
}

export function gemerkteAnsicht(): "spalten" | "raster" {
  try { return localStorage.getItem(ANSICHT_SCHLUESSEL) === "raster" ? "raster" : "spalten"; }
  catch { return "spalten"; }
}

export function merkeAnsicht(a: "spalten" | "raster"): void {
  schreib(ANSICHT_SCHLUESSEL, a);
}

/**
 * Der gemerkte Filter oder die Vorgabe. Ein Unternehmen, das es nicht (mehr) gibt, fällt auf
 * „Alle" zurück — aber nur, wenn die Unternehmensliste schon da ist (eine leere Liste beim
 * ersten Zeichnen soll die Wahl nicht verwerfen).
 */
export function leseFilter(companyIds: readonly string[]): BoardFilter {
  const w = lies(FILTER_SCHLUESSEL);
  if (typeof w !== "object" || w === null) return LEERER_FILTER;
  const k = w as Record<string, unknown>;
  if (typeof k.company !== "string" || typeof k.customer !== "string"
      || typeof k.assignee !== "string" || typeof k.linked !== "boolean") return LEERER_FILTER;
  const company = k.company === "ALLE" || companyIds.length === 0 || companyIds.includes(k.company)
    ? k.company : "ALLE";
  return { company, customer: k.customer, assignee: k.assignee, linked: k.linked };
}

export function merkeFilter(f: BoardFilter): void {
  schreib(FILTER_SCHLUESSEL, JSON.stringify({ company: f.company, customer: f.customer,
                                              assignee: f.assignee, linked: f.linked }));
}

/** Auf-/Zuklappen je Spalte (Spalten-Id → offen); unbrauchbare Einträge fallen weg. */
export function leseOffen(): Record<string, boolean> {
  const w = lies(OFFEN_SCHLUESSEL);
  if (typeof w !== "object" || w === null || Array.isArray(w)) return {};
  const offen: Record<string, boolean> = {};
  for (const [id, wert] of Object.entries(w as Record<string, unknown>)) {
    if (typeof wert === "boolean") offen[id] = wert;
  }
  return offen;
}

export function merkeOffen(offen: Record<string, boolean>): void {
  schreib(OFFEN_SCHLUESSEL, JSON.stringify(offen));
}
